// Labels for the AI Mapping page's own header, plus the link back to the task.
//
// The target label map itself lives in src/utils/aiIngestTargets.js: the task page displays the same
// labels on its ingest history, and neither page owns the concept. Re-exported here so this module stays
// the one import the aiDataMapping components need.
//
// Pure and standalone so it can be asserted directly, following describeIngestError.js. NOT in
// utils/index.js, which Vitest cannot parse (see frontend/CLAUDE.md).

import { Flame, Lightning, Truck } from "@phosphor-icons/react";

export { TARGET_LABELS, formatTarget } from "../../../utils/aiIngestTargets";

// Same scope → icon/color pairing as the task cards on the Organization Planning tab
// (DataEntryPlanning/components/OrganizationPlanning/components/TaskCard.jsx's SCOPE_COLORS) — this is the
// one other place in the app that badges a scope. Exported (moved from TaskBar.jsx, which still uses it)
// because CommitPanel.jsx's header now wants the same pill. No JSX here — these are component
// REFERENCES, not elements — so this stays a plain, Vitest-parseable .js file.
export const SCOPE_PILLS = {
  SCOPE_1: { bg: "#E8FAF0", color: "#0B5E30", icon: Flame },
  SCOPE_2: { bg: "#E8F4FD", color: "#1A5276", icon: Lightning },
  SCOPE_3: { bg: "#F3E8FE", color: "#5B1D99", icon: Truck },
};

export function scopePillFor(aiIngestTarget) {
  const scope = aiIngestTarget?.match(/^SCOPE_\d/)?.[0];
  return scope ? SCOPE_PILLS[scope] ?? null : null;
}

/**
 * The task's reporting period, from the descriptor's `year` / `quarter` metadata.
 *
 * "ALL" is a real quarter meaning the whole year, so it renders as "FY2027 ALL" rather than being
 * hidden — a user looking at an annual task should see that it is annual, not an unqualified year.
 */
export function formatPeriod(descriptor) {
  if (!descriptor) return "";
  const { year, quarter } = descriptor;
  if (year === null || year === undefined) return quarter ? String(quarter) : "";
  return quarter ? `FY${year} ${quarter}` : `FY${year}`;
}

/** Where the AI Mapping page came from, and where "Back to task" returns to. */
export function taskHref(basePath, questionnaireId) {
  // `?id=` is the convention every other link to this page uses (six call sites), not a choice made here.
  return `${basePath}?id=${encodeURIComponent(String(questionnaireId))}`;
}

// Matches the description Java writes for a dimension level the TASK pins (AiIngestTemplateService.java,
// dimensionFields()): `Dimension level "%s", fixed by this task to "%s". Emit that value...`.
//
// This is a stopgap, not a contract: the level's human name is only reachable by parsing that prose
// because AiIngestTemplateFieldDto has no dedicated pinned-dimension field, and if Java ever reword the
// sentence this silently starts returning nothing instead of failing loudly. See the note this drives in
// TaskBar.jsx — it exists precisely so this is visible on the page, not just in a code comment.
const PINNED_DIMENSION_RE = /^Dimension level "([^"]+)", fixed by this task to/;

/**
 * The task's pinned dimension levels — e.g. a Region locked to "Europe" — read from the same
 * `descriptor.fields` TaskBar already fetches for the period.
 *
 * `allowed_values` is used for the value rather than parsing it out of the description too: a pinned
 * field is narrowed to exactly the pinned item(s) server-side (see the Java comment on `levelIsPinned`),
 * so `allowed_values` already IS the fixed value — no extra parsing needed for that half.
 */
export function pinnedDimensions(descriptor) {
  const fields = descriptor?.fields;
  if (!Array.isArray(fields)) return [];
  return fields
    .map((field) => {
      const match = PINNED_DIMENSION_RE.exec(field?.description || "");
      if (!match) return null;
      const values = Array.isArray(field.allowed_values) ? field.allowed_values : [];
      if (!values.length) return null;
      return { level: match[1], value: values.join(", ") };
    })
    .filter(Boolean);
}
